import { BUSINESS_TIMEZONE } from '@omecare/utils';
import type { Locale } from 'date-fns';
import { format, isSameDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { capitalizeFirst } from '@/lib/capitalize-first';

/** Separator between the two ends of a time range, as in the mockup. */
const RANGE_SEPARATOR = '–';

/** Arrow used when a slot spans several days. */
const DAY_SPAN_SEPARATOR = '→';

const FULL_DATE_PATTERN = 'EEEE d MMMM yyyy';
const SHORT_DATE_TIME_PATTERN = "d MMM yyyy' · 'HH:mm";
const TIME_PATTERN = 'HH:mm';

/** Date and time of a slot, split for the two lines of a sheet header. */
export type SlotSchedule = {
  /** Day the slot happens on, or the full span when it crosses midnight. */
  dateLabel: string;
  /** Time range, or `null` for all-day and multi-day slots. */
  timeLabel: string | null;
};

/** Two-digit hour, so "8 – 14" reads as "08:00 – 14:00". */
const HOUR_PADDING = 2;

/**
 * Formats the fixed hour range of a period of the day, as the slot-creation
 * forms show it next to the period name.
 *
 * @param range - the period's start and end hour, or `null` for no fixed range
 * @returns the `HH:00 – HH:00` label, or `null` when the period has no range
 */
export const formatPeriodHourRange = (
  range: { start: number; end: number } | null,
): string | null => {
  if (!range) {
    return null;
  }
  const start = String(range.start).padStart(HOUR_PADDING, '0');
  const end = String(range.end).padStart(HOUR_PADDING, '0');
  return `${start}:00 ${RANGE_SEPARATOR} ${end}:00`;
};

/**
 * Formats a slot's period the way the mockup header shows it: a capitalised
 * weekday line plus a separate time range. Multi-day slots collapse both ends
 * into the date line, since a bare time range would be ambiguous there.
 * Both ends read in the office's timezone, never the viewer's.
 *
 * @param params.start - first instant of the slot
 * @param params.end - last instant of the slot, when known
 * @param params.allDay - whether the slot covers the whole day
 * @param params.locale - date-fns locale matching the active language
 * @returns the date and time labels to render
 */
export const formatSlotSchedule = ({
  start,
  end,
  allDay,
  locale,
}: {
  start: Date;
  end: Date | null;
  allDay: boolean;
  locale: Locale;
}): SlotSchedule => {
  const officeStart = toZonedTime(start, BUSINESS_TIMEZONE);
  const officeEnd = end ? toZonedTime(end, BUSINESS_TIMEZONE) : null;
  return formatOfficeSchedule({
    start: officeStart,
    end: officeEnd,
    allDay,
    locale,
  });
};

/**
 * Builds the schedule labels from dates already shifted to the office's
 * wall-clock time, so plain `format` reads the office's day and hours.
 *
 * @param params.start - first instant of the slot, in office wall-clock time
 * @param params.end - last instant of the slot, in office wall-clock time
 * @param params.allDay - whether the slot covers the whole day
 * @param params.locale - date-fns locale matching the active language
 * @returns the date and time labels to render
 */
const formatOfficeSchedule = ({
  start,
  end,
  allDay,
  locale,
}: {
  start: Date;
  end: Date | null;
  allDay: boolean;
  locale: Locale;
}): SlotSchedule => {
  if (end && !isSameDay(start, end)) {
    return {
      dateLabel: `${format(start, SHORT_DATE_TIME_PATTERN, { locale })} ${DAY_SPAN_SEPARATOR} ${format(end, SHORT_DATE_TIME_PATTERN, { locale })}`,
      timeLabel: null,
    };
  }

  const dateLabel = capitalizeFirst(
    format(start, FULL_DATE_PATTERN, { locale }),
  );

  if (allDay) {
    return { dateLabel, timeLabel: null };
  }

  if (!end) {
    return { dateLabel, timeLabel: format(start, TIME_PATTERN, { locale }) };
  }

  return {
    dateLabel,
    timeLabel: `${format(start, TIME_PATTERN, { locale })} ${RANGE_SEPARATOR} ${format(end, TIME_PATTERN, { locale })}`,
  };
};
